import React, { FC, ReactElement, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme } from '@mui/material';
import { clsx } from 'clsx';
// Components
import Text from '../text';
import Loader from '../loader';
import Space from '../layout/space';
import RadioButtonGroup from '../form/radioGroup';
import ModalButton, { ButtonType } from './templates/modalButton';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import { PickerOption } from '../../core/apiTypes';
import { DISABLE_TIME } from '../../utils/constants';
import useGlobalStyles from '../hook/useGlobalStyles';

interface SelfServiceProps extends PropsFromModal {
    onConfirmAction?: () => void;
}

const SelfService: FC<SelfServiceProps> = (props): ReactElement => {
    const { onRequestClose, onConfirmAction } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    const [isActive, setActive] = useState<boolean>(false);
    const [videoIndex, setVideoIndex] = useState<string>('1');

    const radioOptions: PickerOption[] = [
        { value: '1', label: stringsRes.selfService.welcome.invite.radio },
        { value: '2', label: stringsRes.selfService.welcome.policy.radio },
    ];

    const onClickConfirm = (): void => {
        if (onConfirmAction) {
            onConfirmAction();
        }
        onRequestClose();
    };

    const onChangeVideo = (_, val): void => {
        if (videoIndex !== val) {
            setVideoIndex((v) => (v === '1' ? '2' : '1'));
        }
    };

    React.useEffect(() => {
        setTimeout(() => {
            setActive(true);
        }, DISABLE_TIME);
    }, []);

    return (
        <div className={clsx('LCSelfServiceContainer', globalClasses.flexRow)}>
            <div className={classes.selfServiceContainerLeft}>
                <Text.H15 text={stringsRes.selfService.welcome.title} />
                <Space value={2} />
                <RadioButtonGroup
                    title={stringsRes.selfService.welcome.videoTuto}
                    color="secondary"
                    name="selector"
                    options={radioOptions}
                    onChange={onChangeVideo}
                    value={videoIndex}
                />
                <div style={{ flex: 1 }} />
                <div className={classes.ssContainerContinue}>
                    <ModalButton
                        type={isActive ? ButtonType.CONFIRM : ButtonType.SKIP}
                        onClick={onClickConfirm}
                        label={stringsRes.common.continue}
                        disabled={!isActive}
                    />
                    {!isActive && (
                        <>
                            <Space value={1} />
                            <Text.ShortRegularText12 text={stringsRes.selfService.welcome.creating} />
                            <Space value={1} />
                            <Loader size={20} />
                        </>
                    )}
                </div>
            </div>
            <div className={classes.selfServiceContainerRight}>
                <div className={classes.ssContainerStepVideo}>
                    <video
                        width="100%"
                        height="100%"
                        autoPlay
                        controls
                        muted
                        hidden={+videoIndex !== 1}
                        // loop
                        onEndedCapture={(e) => {
                            onChangeVideo(e, 2);
                        }}
                    >
                        <source src="demo1.mp4" type="video/mp4" />
                        {/* You can add multiple source elements for different formats */}
                        {/* <source src="demo.webm" type="video/webm" /> */}
                        {stringsRes.errors.video}
                    </video>
                    <video width="100%" height="100%" autoPlay controls muted hidden={+videoIndex === 1} loop>
                        <source src="demo2.mp4" type="video/mp4" />
                        {stringsRes.errors.video}
                    </video>
                </div>

                <div className={classes.ssContainerStepDescription}>
                    <Text.H2 text={stringsRes.selfService.welcome[videoIndex === '1' ? 'invite' : 'policy'].radio} />
                    <Space value={1} />
                    <Text.Description14
                        text={stringsRes.selfService.welcome[videoIndex === '1' ? 'invite' : 'policy'].description}
                    />
                </div>
            </div>
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        filterInput: {
            '& .MuiFormControlLabel-root': {
                marginTop: 8,
                marginBottom: 8,
            },
            '& .MuiTypography-root': {
                fontSize: 16,
                fontWeight: 400,
                color: theme.palette.primary.main,
            },
        },
        selfServiceContainerLeft: {
            display: 'flex',
            flexDirection: 'column',
            padding: '32px 24px',
        },
        ssContainerContinue: {
            textAlign: 'center',
            alignSelf: 'center',
            '& .MuiButtonBase-root': {
                color: 'white',
                fontSize: 12,
                padding: '10px 38px',
            },
            '& .MuiButtonBase-root:disabled': {
                backgroundColor: '#74767D',
                color: 'white',
            },
        },
        selfServiceContainerRight: {
            display: 'flex',
            flexDirection: 'column',
            flex: 4,
        },
        ssContainerStepVideo: {
            // flex: 1,
            // height: '50%',
            display: 'contents',
        },
        ssContainerStepDescription: {
            backgroundColor: theme.palette.colors.background.secondaryElement,
            padding: '42px 50px',
        },
    }),
);

export default SelfService;
